import { AxiosInstance } from 'axios';
import { toast } from 'sonner';

import { CAPABILITIES } from '@/sections/asset/constants';
import { JobConfig } from '@/sections/asset/types';
import { GraphQuery, Port } from '@/types';

interface PortQueryResponse {
  ports?: Port[];
}

/** Graph query for the Ports that an Asset HAS_PORT to. */
function portsForAssetQuery(assetKey: string): GraphQuery {
  return {
    node: {
      labels: ['Port'],
      relationships: [
        {
          label: 'HAS_PORT',
          source: {
            labels: ['Asset'],
            filters: [{ field: 'key', operator: '=', value: assetKey }],
          },
        },
      ],
    },
  };
}

/**
 * Resolves a single asset to nuclei jobs, one per open port.
 *
 * nuclei runs against a Port target (not an Asset), so a bare asset must be
 * fanned out to every discovered port. A `#port#` key is scanned directly.
 */
async function resolveAssetPortsForScan(
  asset: { key: string },
  axios: AxiosInstance
): Promise<JobConfig[]> {
  if (asset.key.startsWith('#port#')) {
    return [{ assetKey: asset.key, capabilities: [CAPABILITIES.NUCLEI] }];
  }

  try {
    const { data } = await axios.post<PortQueryResponse>(
      '/my',
      portsForAssetQuery(asset.key),
      { params: { tree: false } }
    );

    return (data.ports ?? []).map(port => ({
      assetKey: port.key,
      capabilities: [CAPABILITIES.NUCLEI],
    }));
  } catch {
    toast.error(`Failed to fetch ports for asset ${asset.key}`);
    return [];
  }
}

/**
 * Builds nuclei jobs for assets by fanning each asset out to its open ports.
 */
export async function buildNetworkScanJobs(
  assets: { key: string }[],
  axios: AxiosInstance
): Promise<JobConfig[]> {
  const results = await Promise.allSettled(
    assets.map(asset => resolveAssetPortsForScan(asset, axios))
  );

  return results
    .filter(
      (r): r is PromiseFulfilledResult<JobConfig[]> => r.status === 'fulfilled'
    )
    .flatMap(r => r.value);
}
